import { AnimationEvent, animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeService, IconComponent, NOT_DEF_ICON } from '@pflegix/ui';
import { I18NextModule } from 'angular-i18next';
import { MenuItem } from 'primeng/api';
import { DomHandler } from 'primeng/dom';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  imports: [I18NextModule, NgClass, TooltipModule, IconComponent, RouterLink, RouterLinkActive],
  template: `
    @if (item['label'] || 'unknown' | i18next: { ns: 'navigation' }; as label) {
      @if (root && item.visible !== false) {
        <div class="layout-menuitem-root-text">
          @if (!item.routerLink && !item.url) {
            <span>{{ label }}</span>
          }
          @if (item.routerLink) {
            <a
              (click)="itemClick($event)"
              (mouseenter)="onMouseEnter()"
              [ngClass]="item['class']"
              [routerLink]="item.routerLink"
              routerLinkActive="active-route"
              [routerLinkActiveOptions]="
                item.routerLinkActiveOptions || {
                  paths: 'subset',
                  queryParams: 'ignored',
                  matrixParams: 'ignored',
                  fragment: 'ignored',
                }
              "
              [fragment]="item.fragment"
              [queryParamsHandling]="item.queryParamsHandling"
              [preserveFragment]="item.preserveFragment"
              [skipLocationChange]="item.skipLocationChange"
              [replaceUrl]="item.replaceUrl"
              [state]="item.state"
              [queryParams]="item.queryParams"
              [attr.target]="item.target"
              tabindex="0"
              [pTooltip]="label"
              [tooltipDisabled]="!(isSlim && root)"
              >{{ label }}</a
            >
          }
          @if (item.url) {
            <a
              [attr.href]="item.url"
              (click)="itemClick($event)"
              (mouseenter)="onMouseEnter()"
              [ngClass]="item['class']"
              [attr.target]="item.target"
              tabindex="0"
              [pTooltip]="label"
              [tooltipDisabled]="!(isSlim && root && !active)"
              >{{ label }}</a
            >
          }
          <i class="layout-menuitem-root-icon pi pi-fw pi-ellipsis-h"></i>
        </div>
      }
      @if ((!item.routerLink || item.items) && item.visible !== false) {
        <a
          [attr.href]="item.url"
          (click)="itemClick($event)"
          (mouseenter)="onMouseEnter()"
          [ngClass]="item['class']"
          [attr.target]="item.target"
          tabindex="0"
          [pTooltip]="label"
          [tooltipDisabled]="!(isSlim && root && !active)"
        >
          @if (item.icon) {
            <pfx-icon
              [icon]="iconProp"
              class="layout-menuitem-icon"
            ></pfx-icon>
          }
          <span class="layout-menuitem-text">{{ label }}</span>
          @if (item.items) {
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          }
        </a>
      }
      @if (item.routerLink && !item.items && item.visible !== false) {
        <a
          (click)="itemClick($event)"
          (mouseenter)="onMouseEnter()"
          [ngClass]="item['class']"
          [routerLink]="item.routerLink"
          routerLinkActive="active-route"
          [routerLinkActiveOptions]="
            item.routerLinkActiveOptions || {
              paths: 'subset',
              queryParams: 'ignored',
              matrixParams: 'ignored',
              fragment: 'ignored',
            }
          "
          [fragment]="item.fragment"
          [queryParamsHandling]="item.queryParamsHandling"
          [preserveFragment]="item.preserveFragment"
          [skipLocationChange]="item.skipLocationChange"
          [replaceUrl]="item.replaceUrl"
          [state]="item.state"
          [queryParams]="item.queryParams"
          [attr.target]="item.target"
          tabindex="0"
          [pTooltip]="label"
          [tooltipDisabled]="!(isSlim && root)"
        >
          <pfx-icon
            [icon]="iconProp"
            class="layout-menuitem-icon"
          ></pfx-icon>
          <span class="layout-menuitem-text">{{ label }}</span>
          @if (item.items) {
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          }
        </a>
      }
      @if (item.items && item.visible !== false) {
        <ul
          #submenu
          [@children]="submenuAnimation"
          (@children.done)="onSubmenuAnimated($event)"
        >
          @for (child of item.items; track child; let i = $index) {
            <li
              app-menuitem
              [item]="child"
              [index]="i"
              [parentKey]="key"
              [class]="child['badgeClass']"
            ></li>
          }
        </ul>
      }
    }
  `,
  styleUrls: ['./app.menuitem.component.scss'],
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
        })
      ),
      state(
        'hidden',
        style({
          display: 'none',
        })
      ),
      state(
        'visible',
        style({
          display: 'block',
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
})
export class AppMenuitemComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly layoutService = inject(LayoutService);
  public readonly router = inject(Router);
  private readonly menuService = inject(MenuService);

  @Input() public item!: MenuItem;

  public readonly index = input.required<number>();

  @Input() @HostBinding('class.layout-root-menuitem') public root!: boolean;

  public readonly parentKey = input<string>();

  @ViewChild('submenu') public submenu!: ElementRef;

  protected active = false;

  protected menuSourceSubscription: Subscription;

  protected menuResetSubscription: Subscription;

  protected key = '';

  protected iconProp: IconProp = NOT_DEF_ICON;

  protected readonly fontAwesomeService = inject(FontAwesomeService);

  constructor() {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = !!(value.key === this.key || value.key.startsWith(`${this.key}-`));
        } else if (value.key !== this.key && !value.key.startsWith(`${this.key}-`)) {
          this.active = false;
        }
      });
    });

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
      if (this.isSlimPlus || this.isSlim || this.isHorizontal) {
        this.active = false;
      } else if (this.item.routerLink) {
        this.updateActiveStateFromRoute();
      }
    });
  }

  public ngOnInit(): void {
    const parentKey = this.parentKey();

    this.key = parentKey ? `${parentKey}-${this.index()}` : String(this.index());

    if (!(this.isSlimPlus || this.isSlim || this.isHorizontal) && this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }

    if (this.item.icon) {
      this.iconProp = this.fontAwesomeService.getIconDefinition(this.item.icon) ?? NOT_DEF_ICON;
    }
  }

  public ngAfterViewChecked(): void {
    if (
      this.root &&
      this.active &&
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())
    ) {
      this.calculatePosition(this.submenu?.nativeElement, this.submenu?.nativeElement.parentElement);
    }
  }

  protected updateActiveStateFromRoute(): void {
    const activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  protected onSubmenuAnimated(event: AnimationEvent): void {
    if (
      event.toState === 'visible' &&
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())
    ) {
      const el = <HTMLUListElement>event.element;
      const elParent = <HTMLUListElement>el.parentElement;

      this.calculatePosition(el, elParent);
    }
  }

  protected calculatePosition(overlay: HTMLElement, target: HTMLElement): void {
    if (overlay) {
      const { left, top } = target.getBoundingClientRect();
      const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
      const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
      const scrollbarWidth = DomHandler.calculateScrollbarWidth();
      const topbarEl = document.querySelector('.layout-topbar') as HTMLElement;
      const topbarHeight = topbarEl?.offsetHeight || 0;

      // reset
      // eslint-disable-next-line no-param-reassign
      overlay.style.top = '';
      // eslint-disable-next-line no-param-reassign
      overlay.style.left = '';

      if (this.layoutService.isHorizontal()) {
        const width = left + oWidth + scrollbarWidth;

        // eslint-disable-next-line no-param-reassign
        overlay.style.left = vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
      } else if (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) {
        const topOffset = top - topbarHeight;
        const height = topOffset + oHeight + topbarHeight;

        // eslint-disable-next-line no-param-reassign
        overlay.style.top = vHeight < height ? `${topOffset - (height - vHeight)}px` : `${topOffset}px`;
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected itemClick(event: Event): void {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    if ((this.root && this.isSlim) || this.isHorizontal || this.isSlimPlus) {
      this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
    }

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item.items) {
      this.active = !this.active;

      if (this.root && this.active && (this.isSlim || this.isHorizontal || this.isSlimPlus)) {
        this.layoutService.onOverlaySubmenuOpen();
      }
    } else {
      if (this.layoutService.isMobile()) {
        this.layoutService.state.staticMenuMobileActive = false;
      }

      if (this.isSlim || this.isHorizontal || this.isSlimPlus) {
        this.menuService.reset();
        this.layoutService.state.menuHoverActive = false;
      }
    }

    this.menuService.onMenuStateChange({ key: this.key });
  }

  protected onMouseEnter(): void {
    // activate item on hover
    if (!(this.root && (this.isSlim || this.isHorizontal || this.isSlimPlus) && this.layoutService.isDesktop())) {
      return;
    }

    if (this.layoutService.state.menuHoverActive) {
      this.active = true;
      this.menuService.onMenuStateChange({ key: this.key });
    }
  }

  protected get submenuAnimation(): string {
    if (
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus())
    ) {
      return this.active ? 'visible' : 'hidden';
    }

    if (this.root || this.active) {
      return 'expanded';
    }

    return 'collapsed';
  }

  protected get isHorizontal(): boolean {
    return this.layoutService.isHorizontal();
  }

  protected get isSlim(): boolean {
    return this.layoutService.isSlim();
  }

  protected get isSlimPlus(): boolean {
    return this.layoutService.isSlimPlus();
  }

  @HostBinding('class.active-menuitem')
  protected get activeClass(): boolean {
    return this.active && !this.root;
  }

  public ngOnDestroy(): void {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
